/**
 * Token Management Utilities for Microsoft Entra ID Authentication
 *
 * This module provides centralized token management functionality including
 * token acquisition, refresh logic, error handling, and retry mechanisms.
 * Extracted from useEntraAuth.ts for better separation of concerns.
 */

import {
  InteractionRequiredAuthError,
  SilentRequest,
  IPublicClientApplication,
  AccountInfo,
  BrowserAuthError,
} from '@azure/msal-browser';
import { NetworkError, ServerError } from '@azure/msal-common';
import { authLogger } from './logger';
import { TOKEN_RETRY_DELAY, FLOW_DELAYS } from '../constants';
import { silentRequest } from '../msalConfig';

/**
 * Token acquisition result with expiration information
 */
export interface TokenResult {
  accessToken: string;
  expiresOn?: Date;
}

/**
 * Helper function to create a silent token request
 *
 * @param account - The account to create the request for
 * @returns SilentRequest object configured for the account
 */
export function createSilentTokenRequest(
  account: AccountInfo,
  forceRefresh?: boolean,
): SilentRequest {
  return {
    ...silentRequest,
    account,
    ...(forceRefresh !== undefined && { forceRefresh }),
  };
}

/**
 * Helper function to determine if a token refresh error should be retried
 *
 * @param error - The error that occurred during token refresh
 * @returns True if the error is recoverable and should be retried
 */
export function shouldRetryTokenRefresh(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) {
    return false;
  }

  if (error instanceof NetworkError) {
    const httpStatus = error.httpStatus;
    if (httpStatus && httpStatus >= 500 && httpStatus < 600) {
      return true;
    }
    if (!httpStatus) {
      return true;
    }
    return false;
  }

  if (error instanceof ServerError) {
    const status = error.status;
    if (status && status >= 500 && status < 600) {
      return true;
    }
    return false;
  }

  if (error instanceof BrowserAuthError) {
    return false;
  }

  return false;
}

/**
 * Helper function to handle and log token refresh errors
 *
 * @param error - The error that occurred during token refresh
 * @returns void - This function only logs errors and does not return a value
 */
export function handleTokenRefreshError(error: unknown): void {
  if (error instanceof InteractionRequiredAuthError) {
    return;
  }

  if (error instanceof NetworkError) {
    const httpStatus = error.httpStatus;
    if (httpStatus && httpStatus >= 500 && httpStatus < 600) {
      authLogger.warn(
        'Token refresh failed due to server error (recoverable):',
        error.message,
      );
    } else if (!httpStatus) {
      authLogger.warn(
        'Token refresh failed due to network connectivity (recoverable):',
        error.message,
      );
    } else {
      authLogger.error(
        'Token refresh failed due to client error (non-recoverable):',
        error.message,
      );
    }
    return;
  }

  if (error instanceof ServerError) {
    const status = error.status;
    if (status && status >= 500 && status < 600) {
      authLogger.warn(
        'Token refresh failed due to server error (recoverable):',
        error.message,
      );
    } else {
      authLogger.error(
        'Token refresh failed due to client error (non-recoverable):',
        error.message,
      );
    }
    return;
  }

  if (error instanceof BrowserAuthError) {
    authLogger.error(
      'Token refresh failed due to browser error (non-recoverable):',
      error.message,
    );
    return;
  }

  authLogger.error('Token refresh failed due to unknown error:', error);
}

/**
 * Helper function to attempt token acquisition with popup fallback
 * Returns both token and expiration information for lifecycle management
 *
 * @param instance - The MSAL instance
 * @param account - The account to acquire token for
 * @returns Token result with access token and expiration, or undefined if failed
 */
export async function acquireTokenWithFallback(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<TokenResult | undefined> {
  const request = createSilentTokenRequest(account);

  try {
    const response = await instance.acquireTokenSilent(request);
    return {
      accessToken: response.accessToken,
      expiresOn: response.expiresOn ?? undefined,
    };
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const popupRequest = createSilentTokenRequest(account);
        const response = await instance.acquireTokenPopup(popupRequest);
        return {
          accessToken: response.accessToken,
          expiresOn: response.expiresOn ?? undefined,
        };
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
}

/**
 * Helper function to attempt silent token refresh with intelligent retry logic
 *
 * @param instance - The MSAL instance
 * @param account - The account to refresh token for
 * @param onTokenRefreshed - Optional callback when token is successfully refreshed
 * @returns True if token refresh was successful, false otherwise
 */
export async function attemptSilentTokenRefresh(
  instance: IPublicClientApplication,
  account: AccountInfo,
  onTokenRefreshed?: () => void,
  forceRefresh?: boolean,
): Promise<boolean> {
  const maxRetries = 1;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const request = createSilentTokenRequest(account, forceRefresh);
      const response = await instance.acquireTokenSilent(request);

      if (response?.account) {
        instance.setActiveAccount(response.account);
        // Log successful token refresh in development mode
        authLogger.dev(
          '✅ Token refreshed successfully, active account updated with new expiration',
        );

        setTimeout(() => {
          // Log user info refresh trigger in development mode
          authLogger.dev('🔄 Triggering user info refresh after token update');
          onTokenRefreshed?.();
        }, FLOW_DELAYS.USER_INFO_REFRESH);
      }

      return true;
    } catch (error) {
      lastError = error;

      const shouldRetry = shouldRetryTokenRefresh(error);

      if (attempt < maxRetries && shouldRetry) {
        await new Promise((resolve) => setTimeout(resolve, TOKEN_RETRY_DELAY));
        continue;
      }

      break;
    }
  }

  handleTokenRefreshError(lastError);
  return false;
}

/**
 * Get current token expiration from active account
 *
 * @param instance - The MSAL instance
 * @param accounts - Array of available accounts
 * @returns Token expiration date, or undefined if not available
 */
export function getCurrentTokenExpiration(
  instance: IPublicClientApplication,
  accounts: AccountInfo[],
): Date | undefined {
  const currentAccount = instance.getActiveAccount() ?? accounts[0] ?? null;
  if (!currentAccount?.idTokenClaims?.exp) return undefined;

  // Convert Unix timestamp to Date
  return new Date(currentAccount.idTokenClaims.exp * 1000);
}

/**
 * Get the current account from MSAL instance or accounts array
 *
 * @param instance - The MSAL instance
 * @param accounts - Array of available accounts
 * @returns Current account, or null if none available
 */
export function getCurrentAccount(
  instance: IPublicClientApplication,
  accounts: AccountInfo[],
): AccountInfo | null {
  return instance.getActiveAccount() ?? accounts[0] ?? null;
}
